import { AlertTriangle, CalendarOff, Crown, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAssignmentCandidates, type AssignmentCheckParams } from '@/hooks/jobs';
import { formatDuration, formatWallTime } from '@/lib/dates';
import { cn } from '@/lib/utils';
import type { AssignmentCandidate } from '@shared/schemas/job';

/*
 * Who goes on the job — ROADMAP.md §4.5, §7.3 and §7.4, shared by the create
 * dialog and the reassignment dialog so the warnings cannot exist in one and not
 * the other.
 *
 * **The list arrives ranked and nothing here re-sorts it.** business-rules.md §4
 * scores availability, history with this client, skills, position and load
 * balancing, and the server is where that lives — a second ordering in the
 * browser would be a second opinion.
 *
 * **Never auto-assign silently** (§4). "Suggest best match" ticks the top
 * candidate and nothing else; the crew is not saved until a human presses Save
 * on the dialog around this, and every suggestion shows the reasons behind it so
 * the admin can disagree. A ranking with no reasons is a black box the office
 * stops trusting the first time it is wrong.
 *
 * Three kinds of caution, deliberately styled apart because their fixes differ:
 *
 *   - **At their daily limit** disables the row. `max_jobs_per_day` is a hard
 *     ceiling and the server refuses it anyway; a checkbox that lets you pick a
 *     person the save will bounce is a form that argues with itself.
 *   - **A time conflict warns and allows.** Two short jobs can genuinely share
 *     a window, and the admin knows things the overlap rule does not.
 *   - **Not working, or working different hours** warns and allows too (7.3).
 *     A cleaner picking up a Saturday is a phone call, not a schema change. The
 *     sentence comes from the resolver, so it names *why* — "Marked unavailable
 *     on 14 Aug 2026 — Vacation" rather than a red dot.
 *
 * Selection order is the crew order and **the first person is the lead**. The
 * "Make lead" button reorders without unpicking anybody.
 */

export function AssignmentPicker({
  params,
  value,
  onChange,
}: {
  /** Null until the dialog has a real date and time. */
  params: AssignmentCheckParams | null;
  /** Employee ids in crew order — the first is the lead. */
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const candidates = useAssignmentCandidates(params);

  if (params === null) {
    return (
      <p className="bg-paper-50 rounded-sm border border-ink-300 px-3 py-2.5 text-meta text-ink-500">
        Pick a date and time first — availability depends on them.
      </p>
    );
  }

  if (candidates.isPending) {
    return (
      <div aria-hidden="true" className="space-y-2">
        {[0, 1, 2].map((row) => (
          <div key={row} className="bg-paper-100 h-11 animate-pulse rounded-sm" />
        ))}
      </div>
    );
  }

  if (candidates.error) {
    return (
      <p role="alert" className="text-meta text-status-priority-text">
        Could not load who is available. Close the dialog and try again.
      </p>
    );
  }

  const list = candidates.data?.data ?? [];

  if (list.length === 0) {
    return (
      <p className="bg-paper-50 rounded-sm border border-ink-300 px-3 py-2.5 text-meta text-ink-500">
        No active employees yet. Add one under People → Employees first.
      </p>
    );
  }

  function toggle(employeeId: string) {
    onChange(
      value.includes(employeeId) ? value.filter((id) => id !== employeeId) : [...value, employeeId],
    );
  }

  function makeLead(employeeId: string) {
    onChange([employeeId, ...value.filter((id) => id !== employeeId)]);
  }

  // The first candidate the ranking did not disqualify. There may be none —
  // before anybody has hours set, everyone is disqualified as unavailable.
  const best = list.find((candidate) => !candidate.isDisqualified);
  const suggestedAlready = best !== undefined && value.includes(best.employeeId);

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-meta text-ink-500">
          Ranked best first. The reasons are underneath each name — disagree with them and pick
          somebody else.
        </p>
        {best === undefined ? null : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={suggestedAlready}
            onClick={() => {
              // Ticks one box. **It does not save** — the dialog's own button
              // does, and a human presses it (business-rules.md §4).
              onChange([...value, best.employeeId]);
            }}
          >
            <Sparkles aria-hidden="true" />
            {suggestedAlready ? `${best.name} is on it` : `Suggest ${best.name}`}
          </Button>
        )}
      </div>

      <ul className="space-y-2">
        {list.map((candidate, index) => {
          const selected = value.includes(candidate.employeeId);
          const isLead = value[0] === candidate.employeeId;
          // Already-selected people stay selectable even at the cap: the cap
          // counts *other* jobs, and unpicking someone must always be possible.
          const blocked = candidate.atDailyCap && !selected;

          return (
            <li key={candidate.employeeId}>
              <div
                className={cn(
                  'rounded-sm border px-3 py-2',
                  selected ? 'border-honey-500 bg-honey-50' : 'border-ink-300 bg-white',
                  blocked && 'opacity-60',
                )}
              >
                <div className="flex items-center gap-2.5">
                  <label className="flex min-h-touch min-w-0 flex-1 cursor-pointer items-center gap-2.5">
                    <input
                      type="checkbox"
                      name={`assign-${candidate.employeeId}`}
                      checked={selected}
                      disabled={blocked}
                      onChange={() => {
                        toggle(candidate.employeeId);
                      }}
                      className="size-4 shrink-0 accent-honey-500"
                    />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 text-body font-medium text-ink-900">
                        {candidate.name}
                        {isLead ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-honey-400 px-2 py-0.5 text-meta font-medium text-ink-900">
                            <Crown aria-hidden="true" className="size-3" />
                            Lead
                          </span>
                        ) : null}
                        {/*
                        Only on the top of the ranking. A rank number beside
                        every name turns a suggestion into a scoreboard, and the
                        office is choosing a person, not reading a league table.
                      */}
                        {index === 0 && !candidate.isDisqualified ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-honey-50 px-2 py-0.5 text-meta font-medium text-bark-700 ring-1 ring-honey-500">
                            Best match
                          </span>
                        ) : null}
                      </span>
                      <span className="block text-meta text-ink-500">
                        {candidate.jobsThatDay === 0
                          ? 'Nothing else that day'
                          : `${String(candidate.jobsThatDay)} of ${String(candidate.maxJobsPerDay)} jobs that day`}
                        {/*
                        The hours they are down for, beside the job count. Only
                        when they are actually working — for somebody who is
                        not, the warning below says so in a full sentence.
                      */}
                        {candidate.availability.isAvailable &&
                        candidate.availability.startTime !== null &&
                        candidate.availability.endTime !== null ? (
                          <>
                            {' · '}
                            {formatWallTime(candidate.availability.startTime)}–
                            {formatWallTime(candidate.availability.endTime)}
                          </>
                        ) : null}
                      </span>
                    </span>
                  </label>

                  {selected && !isLead ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        makeLead(candidate.employeeId);
                      }}
                    >
                      Make lead
                    </Button>
                  ) : null}
                </div>

                {blocked ? (
                  <p className="mt-1.5 flex items-start gap-1.5 pl-6 text-meta text-ink-700">
                    <AlertTriangle
                      aria-hidden="true"
                      className="mt-px size-3.5 shrink-0 text-status-priority-text"
                    />
                    At their daily limit of {candidate.maxJobsPerDay} — the limit is set on their
                    employee record.
                  </p>
                ) : null}

                {/*
                Not working that day. **A warning, not a block** — the resolver
                treats "nothing set" as unavailable, so before the office fills
                in `/admin/scheduling` this is every cleaner, and disabling them
                all would make the dialog unusable. The sentence names which
                screen fixes it.
              */}
                {!candidate.availability.isAvailable ? (
                  <p className="mt-1.5 flex items-start gap-1.5 pl-6 text-meta text-ink-700">
                    <CalendarOff
                      aria-hidden="true"
                      className="mt-px size-3.5 shrink-0 text-status-pending-text"
                    />
                    {candidate.availability.explanation} You can still assign them.
                  </p>
                ) : candidate.availability.outsideWorkingHours ? (
                  <p className="mt-1.5 flex items-start gap-1.5 pl-6 text-meta text-ink-700">
                    <CalendarOff
                      aria-hidden="true"
                      className="mt-px size-3.5 shrink-0 text-status-pending-text"
                    />
                    This job falls outside their hours that day (
                    {formatWallTime(candidate.availability.startTime ?? '')}–
                    {formatWallTime(candidate.availability.endTime ?? '')}).
                  </p>
                ) : null}

                {candidate.conflicts.map((conflict) => (
                  <p
                    key={conflict.jobId}
                    className="mt-1.5 flex items-start gap-1.5 pl-6 text-meta text-ink-700"
                  >
                    <AlertTriangle
                      aria-hidden="true"
                      className="mt-px size-3.5 shrink-0 text-status-pending-text"
                    />
                    Overlaps {conflict.jobNumber} for {conflict.clientName} at{' '}
                    {formatWallTime(conflict.scheduledTime)} (
                    {formatDuration(conflict.estimatedDurationMinutes)}) — you can still assign them
                    if the jobs genuinely fit.
                  </p>
                ))}

                {/*
                **The reasons behind the ranking**, which is the whole of
                business-rules.md §4's explainability rule: the admin has to be
                able to disagree with the order, and disagreeing requires
                knowing. Points shown as `+10` / `−2` beside each, because the
                sum is the ordering and hiding the arithmetic would make the
                ranking exactly the black box it exists not to be.

                Zero-point lines are shown too — "no client chosen yet, so
                history was not scored" is a reason the ranking is what it is.
              */}
                {candidate.reasons.length > 0 ? (
                  <ul className="mt-1.5 space-y-0.5 pl-6">
                    {candidate.reasons.map((reason) => (
                      <li key={reason.label} className="flex items-start gap-1.5 text-meta">
                        <span
                          className={cn(
                            'w-8 shrink-0 text-right font-medium tabular-nums',
                            reason.points > 0
                              ? 'text-status-completed-text'
                              : reason.points < 0
                                ? 'text-status-priority-text'
                                : 'text-ink-500',
                          )}
                        >
                          {reason.points > 0 ? `+${String(reason.points)}` : ''}
                          {reason.points < 0 ? `−${String(Math.abs(reason.points))}` : ''}
                          {reason.points === 0 ? '·' : ''}
                        </span>
                        <span className="text-ink-700">{reason.label}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

export type { AssignmentCandidate };
